import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { navLinks, siteInfo } from '@/mocks/news';

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  const isActive = (to: string) => {
    if (to === '/') return location.pathname === '/';
    const clean = to.split('#')[0];
    return clean !== '/' && location.pathname === clean;
  };

  const submitSearch = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const value = keyword.trim();
    navigate(value ? `/?q=${encodeURIComponent(value)}#feed` : '/#feed');
    setSearchOpen(false);
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full border-b transition-colors duration-300 ${
        scrolled
          ? 'border-background-200 bg-background-50/95 backdrop-blur'
          : 'border-transparent bg-background-50/80 backdrop-blur-sm'
      }`}
    >
      <div className="mx-auto flex h-16 w-full items-center gap-4 px-4 md:px-6">
        <Link to="/" className="flex shrink-0 items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-background-50">
            <i className="ri-flashlight-fill text-lg" />
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-xl tracking-wide text-foreground-950">
              {siteInfo.name}
            </span>
            <span className="mt-0.5 text-[10px] font-label tracking-[0.18em] text-foreground-500">
              REALTIME GOSSIP FEED
            </span>
          </span>
        </Link>

        <nav aria-label="主导航" className="ml-4 hidden flex-1 items-center gap-1 lg:flex">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`rounded-md px-3 py-2 text-sm font-label whitespace-nowrap transition-colors ${
                isActive(link.to)
                  ? 'bg-background-200 text-primary-400'
                  : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-950'
              }`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            aria-label="搜索"
            onClick={() => setSearchOpen((v) => !v)}
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-400"
          >
            <i className={searchOpen ? 'ri-close-line text-lg' : 'ri-search-line text-lg'} />
          </button>
          <Link
            to="/about#submit"
            className="hidden items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-label text-background-50 transition-colors hover:bg-primary-600 sm:flex"
          >
            <i className="ri-send-plane-fill text-sm" />
            我要投稿
          </Link>
          <button
            type="button"
            aria-label="打开菜单"
            onClick={() => setOpen((v) => !v)}
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-700 lg:hidden"
          >
            <i className={open ? 'ri-close-line text-xl' : 'ri-menu-line text-xl'} />
          </button>
        </div>
      </div>

      {searchOpen ? (
        <div className="border-t border-background-200 bg-background-100 px-4 py-3 md:px-6">
          <form onSubmit={submitSearch} className="mx-auto flex w-full max-w-3xl items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center text-foreground-500">
              <i className="ri-search-2-line text-lg" />
            </span>
            <input
              type="search"
              name="q"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索关键词，如：综艺、电竞、回应"
              className="h-10 flex-1 rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-500 focus:border-primary-400"
            />
            <button
              type="submit"
              className="h-10 shrink-0 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 text-sm font-label text-background-50 transition-colors hover:bg-primary-600"
            >
              搜索
            </button>
          </form>
        </div>
      ) : null}

      {open ? (
        <div className="border-t border-background-200 bg-background-50 px-4 pb-5 pt-3 lg:hidden">
          <nav aria-label="移动端导航" className="flex flex-col">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`flex items-center justify-between border-b border-background-200 py-3 text-sm font-label transition-colors ${
                  isActive(link.to) ? 'text-primary-400' : 'text-foreground-700'
                }`}
              >
                {link.label}
                <i className="ri-arrow-right-s-line text-lg text-foreground-500" />
              </Link>
            ))}
          </nav>
          <Link
            to="/about#submit"
            className="mt-4 flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-3 text-sm font-label text-background-50"
          >
            <i className="ri-send-plane-fill" />
            我要投稿
          </Link>
        </div>
      ) : null}
    </header>
  );
}